/**
 * Centralized Configuration Management System
 *
 * This module provides a type-safe, environment-aware configuration system
 * that eliminates hardcoded values throughout the application.
 *
 * Features:
 * - Environment variable support with validation
 * - Type-safe configuration access
 * - Runtime configuration updates
 * - Default values for all environments
 * - Configuration validation
 */

import { getRuntimeEnv } from './runtime-env'

// Environment types for better type safety
type Environment = 'development' | 'staging' | 'production' | 'test'

// Configuration interfaces
interface ServiceEndpoints {
  api: string
  websocket: string
  auth: string
  governance: string
  analytics: string
  grafana: string
  prometheus: string
  elasticsearch: string
}

interface DatabaseConfig {
  host: string
  port: number
  name: string
  ssl: boolean
  connectionString?: string
}

interface SecurityConfig {
  enableCSRF: boolean
  enableHTTPS: boolean
  enableHSTS: boolean
  corsOrigins: string[]
  trustedDomains: string[]
}

interface FeatureFlags {
  enableAnalytics: boolean
  enableGovernance: boolean
  enableRealtimeUpdates: boolean
  enableAdvancedFeatures: boolean
  enableExperimentalFeatures: boolean
}

interface PerformanceConfig {
  apiTimeout: number
  retryAttempts: number
  cacheTimeout: number
  enableServiceWorker: boolean
}

// Main configuration interface
interface ApplicationConfig {
  environment: Environment
  version: string
  buildTimestamp: string

  services: ServiceEndpoints
  database: DatabaseConfig
  security: SecurityConfig
  features: FeatureFlags
  performance: PerformanceConfig

  // Monitoring and observability
  monitoring: {
    enableMetrics: boolean
    enableTracing: boolean
    enableErrorReporting: boolean
    metricsEndpoint?: string
    tracingEndpoint?: string
  }

  // UI Configuration
  ui: {
    theme: 'light' | 'dark' | 'auto'
    language: string
    timezone: string
    dateFormat: string
    enableNotifications: boolean
  }

  // Additional properties for compatibility
  apiUrl?: string
  wsUrl?: string
}

// Environment-specific configuration defaults
const environmentDefaults: Record<Environment, Partial<ApplicationConfig>> = {
  development: {
    services: {
      api: 'https://localhost:8080',
      websocket: 'wss://localhost:8080/v1/streaming/status',
      auth: 'https://localhost:3000',
      governance: 'http://localhost:8080',
      analytics: 'http://localhost:8002',
      grafana: 'http://localhost:3000',
      prometheus: 'http://localhost:9090',
      elasticsearch: 'http://localhost:9200'
    },
    database: {
      host: 'localhost',
      port: 5432,
      name: 'agion_dev',
      ssl: false
    },
    security: {
      enableCSRF: false,
      enableHTTPS: false,
      enableHSTS: false,
      corsOrigins: ['http://localhost:3000', 'http://localhost:3001'],
      trustedDomains: ['localhost']
    },
    features: {
      enableAnalytics: true,
      enableGovernance: true,
      enableRealtimeUpdates: true,
      enableAdvancedFeatures: true,
      enableExperimentalFeatures: true
    },
    monitoring: {
      enableMetrics: false,
      enableTracing: false,
      enableErrorReporting: false
    }
  },

  staging: {
    services: {
      // Staging URLs must be provided via environment variables
      // NO hardcoded defaults - fail fast if not configured
      api: import.meta.env['VITE_STAGING_API_URL'] || import.meta.env.VITE_API_URL,
      websocket: import.meta.env['VITE_STAGING_WS_URL'] || import.meta.env['VITE_WS_URL'],
      auth: import.meta.env['VITE_STAGING_AUTH_URL'] || import.meta.env['VITE_AUTH_SERVICE_URL'],
      governance: import.meta.env['VITE_STAGING_GOVERNANCE_URL'] || import.meta.env['VITE_ORCHESTRATION_SERVICE_URL'],
      analytics: import.meta.env['VITE_STAGING_ANALYTICS_URL'] || import.meta.env['VITE_DATA_SERVICE_URL'],
      grafana: import.meta.env['VITE_STAGING_GRAFANA_URL'] || import.meta.env['VITE_GRAFANA_URL'],
      prometheus: import.meta.env['VITE_STAGING_PROMETHEUS_URL'] || import.meta.env['VITE_METRICS_URL'],
      elasticsearch: import.meta.env['VITE_STAGING_ELASTICSEARCH_URL'] || import.meta.env['VITE_METRICS_URL']
    },
    database: {
      host: import.meta.env['VITE_STAGING_DB_HOST'] || '',
      port: parseInt(import.meta.env['VITE_STAGING_DB_PORT'] || '5432'),
      name: import.meta.env['VITE_STAGING_DB_NAME'] || '',
      ssl: true
    },
    security: {
      enableCSRF: true,
      enableHTTPS: true,
      enableHSTS: true,
      corsOrigins: [import.meta.env['VITE_STAGING_FRONTEND_URL'] || ''],
      trustedDomains: []
    },
    features: {
      enableAnalytics: true,
      enableGovernance: true,
      enableRealtimeUpdates: true,
      enableAdvancedFeatures: true,
      enableExperimentalFeatures: false
    },
    monitoring: {
      enableMetrics: true,
      enableTracing: true,
      enableErrorReporting: true,
      metricsEndpoint: import.meta.env['VITE_STAGING_METRICS_URL'],
      tracingEndpoint: import.meta.env['VITE_STAGING_TRACING_URL']
    }
  },

  production: {
    services: {
      // Production URLs must be provided via environment variables (Kubernetes ConfigMaps)
      // NO hardcoded defaults - fail fast if not configured
      api: import.meta.env.VITE_API_URL,
      websocket: import.meta.env['VITE_WS_URL'],
      auth: import.meta.env['VITE_AUTH_SERVICE_URL'],
      governance: import.meta.env['VITE_ORCHESTRATION_SERVICE_URL'],
      analytics: import.meta.env['VITE_DATA_SERVICE_URL'],
      grafana: import.meta.env['VITE_GRAFANA_URL'],
      prometheus: import.meta.env['VITE_METRICS_URL'],
      elasticsearch: import.meta.env['VITE_METRICS_URL']
    },
    database: {
      // Database configuration not used by frontend - backend only
      host: '',
      port: 5432,
      name: '',
      ssl: true
    },
    security: {
      enableCSRF: true,
      enableHTTPS: true,
      enableHSTS: true,
      corsOrigins: [import.meta.env['VITE_MISSION_CONTROL_URL']],
      trustedDomains: []
    },
    features: {
      enableAnalytics: true,
      enableGovernance: true,
      enableRealtimeUpdates: true,
      enableAdvancedFeatures: true,
      enableExperimentalFeatures: false
    },
    monitoring: {
      enableMetrics: true,
      enableTracing: true,
      enableErrorReporting: true,
      metricsEndpoint: import.meta.env['VITE_METRICS_URL'],
      tracingEndpoint: import.meta.env['VITE_METRICS_URL']
    }
  },

  test: {
    services: {
      api: 'http://localhost:8080',
      websocket: 'ws://localhost:8080/ws',
      auth: 'http://localhost:3000',
      governance: 'http://localhost:8003',
      analytics: 'http://localhost:8002',
      grafana: 'http://localhost:3000',
      prometheus: 'http://localhost:9090',
      elasticsearch: 'http://localhost:9200'
    },
    database: {
      host: 'localhost',
      port: 5432,
      name: 'agion_test',
      ssl: false
    },
    security: {
      enableCSRF: false,
      enableHTTPS: false,
      enableHSTS: false,
      corsOrigins: ['http://localhost:3000'],
      trustedDomains: ['localhost']
    },
    features: {
      enableAnalytics: false,
      enableGovernance: false,
      enableRealtimeUpdates: false,
      enableAdvancedFeatures: false,
      enableExperimentalFeatures: false
    },
    monitoring: {
      enableMetrics: false,
      enableTracing: false,
      enableErrorReporting: false
    }
  }
}

// Base configuration that applies to all environments
const baseConfig: ApplicationConfig = {
  environment: (import.meta.env['VITE_NODE_ENV'] as Environment) || 'development',
  version: import.meta.env['VITE_APP_VERSION'] || '1.0.0',
  buildTimestamp: import.meta.env['VITE_BUILD_TIMESTAMP'] || new Date().toISOString(),

  services: {
    api: '',
    websocket: '',
    auth: '',
    governance: '',
    analytics: '',
    grafana: '',
    prometheus: '',
    elasticsearch: ''
  },

  database: {
    host: '',
    port: 5432,
    name: '',
    ssl: false
  },

  security: {
    enableCSRF: true,
    enableHTTPS: true,
    enableHSTS: true,
    corsOrigins: [],
    trustedDomains: []
  },

  features: {
    enableAnalytics: true,
    enableGovernance: true,
    enableRealtimeUpdates: true,
    enableAdvancedFeatures: false,
    enableExperimentalFeatures: false
  },

  performance: {
    apiTimeout: parseInt(import.meta.env['VITE_API_TIMEOUT'] || '30000'),
    retryAttempts: parseInt(import.meta.env['VITE_RETRY_ATTEMPTS'] || '3'),
    cacheTimeout: parseInt(import.meta.env['VITE_CACHE_TIMEOUT'] || '300000'),
    enableServiceWorker: import.meta.env['VITE_ENABLE_SERVICE_WORKER'] === 'true'
  },

  monitoring: {
    enableMetrics: import.meta.env['VITE_ENABLE_METRICS'] === 'true',
    enableTracing: import.meta.env['VITE_ENABLE_TRACING'] === 'true',
    enableErrorReporting: import.meta.env['VITE_ENABLE_ERROR_REPORTING'] === 'true',
    metricsEndpoint: import.meta.env['VITE_METRICS_ENDPOINT'],
    tracingEndpoint: import.meta.env['VITE_TRACING_ENDPOINT']
  },

  ui: {
    theme: (import.meta.env['VITE_UI_THEME'] as 'light' | 'dark' | 'auto') || 'auto',
    language: import.meta.env['VITE_UI_LANGUAGE'] || 'en',
    timezone: import.meta.env['VITE_UI_TIMEZONE'] || Intl.DateTimeFormat().resolvedOptions().timeZone,
    dateFormat: import.meta.env['VITE_DATE_FORMAT'] || 'yyyy-MM-dd',
    enableNotifications: import.meta.env['VITE_ENABLE_NOTIFICATIONS'] !== 'false'
  }
}

// Configuration validation
function validateConfig(config: ApplicationConfig): string[] {
  const errors: string[] = []

  // Skip validation for mock mode and test environment
  const isMockMode = import.meta.env['VITE_USE_MOCK_DATA'] === 'true' ||
                     import.meta.env['MODE'] === 'mock'
  const isTestEnv = config.environment === 'test'

  if (!isMockMode && !isTestEnv) {
    // Required URLs for all non-test, non-mock environments
    const requiredUrls = [
      { key: 'api', value: config.services.api, name: 'VITE_API_URL' },
      { key: 'auth', value: config.services.auth, name: 'VITE_AUTH_SERVICE_URL' }
    ]

    for (const url of requiredUrls) {
      if (!url.value) {
        errors.push(`${url.name} environment variable is required but not set`)
      }
    }
  }

  if (config.environment === 'production') {
    // Production-specific validations
    if (!config.security.enableHTTPS) {
      errors.push('HTTPS must be enabled in production')
    }

    if (!config.security.enableCSRF) {
      errors.push('CSRF protection must be enabled in production')
    }

    if (!config.monitoring.enableMetrics) {
      errors.push('Metrics should be enabled in production')
    }

    // Additional production URL validations
    if (!config.services.websocket) {
      errors.push('VITE_WS_URL environment variable is required in production')
    }

    if (!config.services.grafana) {
      errors.push('VITE_GRAFANA_URL environment variable is required in production')
    }
  }

  return errors
}

// Create and validate the configuration
function createConfig(): ApplicationConfig {
  // Handle mock mode as development
  let environment = (import.meta.env['VITE_NODE_ENV'] as Environment) || 'development'
  if (environment === 'mock' as any) {
    environment = 'development'
  }

  const envDefaults = environmentDefaults[environment] || environmentDefaults.development

  // Deep merge base config with environment-specific defaults
  const config: ApplicationConfig = {
    ...baseConfig,
    environment,
    services: { ...baseConfig.services, ...(envDefaults.services || {}) },
    database: { ...baseConfig.database, ...(envDefaults.database || {}) },
    security: { ...baseConfig.security, ...(envDefaults.security || {}) },
    features: { ...baseConfig.features, ...(envDefaults.features || {}) },
    monitoring: { ...baseConfig.monitoring, ...(envDefaults.monitoring || {}) }
  }

  // Override with explicit environment variables (runtime injection or build-time)
  const runtimeApiUrl = getRuntimeEnv('VITE_API_URL')
  if (runtimeApiUrl) {
    config.services.api = runtimeApiUrl
  }

  const runtimeWsUrl = getRuntimeEnv('VITE_WS_URL')
  if (runtimeWsUrl) {
    config.services.websocket = runtimeWsUrl
  }

  const runtimeAuthUrl = getRuntimeEnv('VITE_AUTH_URL')
  if (runtimeAuthUrl) {
    config.services.auth = runtimeAuthUrl
  }

  const runtimeAuthServiceUrl = getRuntimeEnv('VITE_AUTH_SERVICE_URL')
  if (runtimeAuthServiceUrl) {
    config.services.auth = runtimeAuthServiceUrl
  }

  const runtimeGrafanaUrl = getRuntimeEnv('VITE_GRAFANA_URL')
  if (runtimeGrafanaUrl) {
    config.services.grafana = runtimeGrafanaUrl
  }

  // Set compatibility properties
  config.apiUrl = config.services.api
  config.wsUrl = config.services.websocket

  // Validate configuration
  const errors = validateConfig(config)
  if (errors.length > 0) {
    // Configuration validation failed - errors should be handled by application
    throw new Error(`Configuration validation failed: ${errors.join(', ')}`)
  }

  return config
}

// Export the configuration instance
export const config = createConfig()

// Export individual configuration sections for convenience
export const { services, database, security, features, performance, monitoring, ui } = config

// Export the environment
export const { environment } = config

// Helper functions
export const isProduction = () => config.environment === 'production'
export const isDevelopment = () => config.environment === 'development'
export const isStaging = () => config.environment === 'staging'
export const isTest = () => config.environment === 'test'

// Configuration update function for runtime changes
export function updateConfig(updates: Partial<ApplicationConfig>): void {
  Object.assign(config, updates)

  // Re-validate after update
  const errors = validateConfig(config)
  if (errors.length > 0) {
    // Configuration validation warnings - should be logged to monitoring service
  }
}

// Feature flag helpers
export const featureFlags = {
  isAnalyticsEnabled: () => config.features.enableAnalytics,
  isGovernanceEnabled: () => config.features.enableGovernance,
  isRealtimeEnabled: () => config.features.enableRealtimeUpdates,
  areAdvancedFeaturesEnabled: () => config.features.enableAdvancedFeatures,
  areExperimentalFeaturesEnabled: () => config.features.enableExperimentalFeatures
}

// Service URL builders
export const serviceUrls = {
  api: (path: string) => `${config.services.api}${path.startsWith('/') ? '' : '/'}${path}`,
  websocket: (path: string) => `${config.services.websocket}${path.startsWith('/') ? '' : '/'}${path}`,
  auth: (path: string) => `${config.services.auth}${path.startsWith('/') ? '' : '/'}${path}`,
  governance: (path: string) => `${config.services.governance}${path.startsWith('/') ? '' : '/'}${path}`,
  analytics: (path: string) => `${config.services.analytics}${path.startsWith('/') ? '' : '/'}${path}`,
  grafana: (path: string) => `${config.services.grafana}${path.startsWith('/') ? '' : '/'}${path}`
}

// Log configuration in development
if (isDevelopment()) {
  // Application configuration loaded successfully
}

export default config
